import React, { Component, PureComponent } from 'react';
import * as html2canvas from 'html2canvas';
import Constants from './Constants.js';
import {
  getRCSOfCoordinate,
  areCoordinatePeers,
  callBackendAPI,
  formatSeconds,
} from './utils.js';
import './App.css';
import { ReactComponent as EraseIcon } from './svg/erase.svg';
import { ReactComponent as NotesIcon } from './svg/notes.svg';
import { ReactComponent as HintIcon } from './svg/hint.svg';
import { ReactComponent as ArrowIcon } from './svg/arrow.svg';
import { ReactComponent as StarIcon } from './svg/star.svg';
import { ReactComponent as TrophyIcon } from './svg/trophy.svg';

const textDict = Constants.TEXT_DICT[navigator.language === 'ja' ? 'ja' : 'en'];
// const textDict = Constants.TEXT_DICT['ja'];

function getRandomInt(max) {
  return Math.floor(Math.random() * Math.floor(max));
}

function dataURItoBlob(dataURI) {
  var byteString = atob(dataURI.split(',')[1]);
  var ab = new ArrayBuffer(byteString.length);
  var ia = new Uint8Array(ab);
  for (var i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: 'image/jpeg' });
}

class Timer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      displayTime: '00:00',
    };
    this.seconds = 0;
    this.interval = null;
  }

  update = () => {
    const seconds = Math.floor((Date.now() - this.startTime) / 1000);
    let displayTime = formatSeconds(seconds);
    this.seconds = seconds;
    this.setState({
      displayTime: displayTime
    });
  };

  play() {
    this.startTime = Date.now();
    if (!this.interval) {
      this.interval = setInterval(this.update, 200);
    }
  }

  pause() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
  }

  componentDidMount() {
    this.play();
  }

  componentWillUnmount() {
    this.pause();
  }

  render() {
    return (
      <div className="timer">
        {this.state.displayTime}
      </div>
    );
  }
}

class Cell extends PureComponent {
  getBackGroundColor() {
    const { isConflict, isPeer, isSameValue, isSelected } = this.props;
    if (isConflict && isPeer && isSameValue) {
      return Constants.CONFLICT_BG_COLOR;
    } else if (isSelected) {
      return Constants.SELECTED_BG_COLOR;
    } else if (isSameValue) {
      return Constants.SAME_VALUE_BG_COLOR;
    } else if (isPeer) {
      return Constants.PEER_BG_COLOR;
    }
    return false;
  }

  getFontColor() {
    const { isConflict, prefilled, value } = this.props;
    if (isConflict && !prefilled) {
      return Constants.CONFLICT_FONT_COLOR;
    } else if (!prefilled && value) {
      return Constants.NORMAL_FONT_COLOR;
    } else if (!prefilled) {
      return Constants.NOTE_CONTROL_NUMBER_COLOR;
    }
    return false;
  }

  render() {
    return (
      <div className="cell" onClick={this.props.onClick} style={{
        backgroundColor: this.getBackGroundColor() || undefined,
        color: this.getFontColor() || undefined,
      }}>
        {
          this.props.value ? this.props.value :
          Array(9).fill().map((_, i) => {
            const number = i + 1;
            return (
              <div key={number} className="note-number">
                {this.props.notes[number] && number}
              </div>
            );
          })
        }
      </div>
    );
  }
}

export class NumberControl extends PureComponent {
  render() {
    return (
      <div
        className="number notClicked"
        onClick={this.props.onClick}
        style={{
          color: this.props.noteMode ? Constants.NOTE_CONTROL_NUMBER_COLOR
               : Constants.CONTROL_NUMBER_COLOR,
          backgroundColor: Constants.CONTROL_NUMBER_BG_COLOR,
          opacity: this.props.filledCount >= 9 ? 0.3 : 'initial',
        }}
      >
        {this.props.number}
      </div>
    );
  }
}

export default class App extends Component {
  constructor(props) {
    super(props);
    this.state = { board: null };
    this.boardRef = React.createRef();
    this.controlsRef = React.createRef();
    let searchParams = new URLSearchParams(window.location.search);
    this.country = searchParams.get('country')
    if (!Constants.SUPPORTED_COUNTRIES.includes(this.country)) {
      this.country = Constants.DEFAULT_COUNTRY;
    }
    this.mode = searchParams.get('mode')
  }

  sendToApp = (_key, _value) => {
    if ((typeof window.AndroidFunction !== 'undefined') && (typeof window.AndroidFunction.sendKibanaLog === 'function')) {
      return window.AndroidFunction.sendKibanaLog(_key, JSON.stringify(_value));
    } else {
      return this.sendToAppIOS(_key, JSON.stringify({[_key]: _value}));
    }
  }

  sendToAppIOS = (_key, _val) => {
    let msgIframe = document.createElement("IFRAME");
    if (msgIframe.setAttribute) {
      msgIframe.setAttribute("src", "tvapp:##kibanaLog##" + _val);
      msgIframe.setAttribute("hidden", "true");
      document.documentElement.appendChild(msgIframe);
      if (msgIframe.parentNode) {
        try {
          msgIframe.parentNode.removeChild(msgIframe);
        } catch (err) {
          console.log("Remove msgIframe failed.");
        }
      }
      msgIframe = null;
    }
  };

  initialize = async (difficulty = 'medium') => {
    this.rowCount = [...Array(9)].map(e => Array(10).fill(0));
    this.colCount = [...Array(9)].map(e => Array(10).fill(0));
    this.sqrCount = [...Array(9)].map(e => Array(10).fill(0));
    this.filledCount = Array(10).fill(0);
    this.selectCell = this.selectCellFunc;
    this.conflicts = 0;
    this.hintsLeft = this.mode === 'infinite_hints' ? 100 : 3;
    this.difficulty = difficulty[0].toUpperCase() + difficulty.slice(1);
    const board = [...Array(9)].map(e => Array(9));
    // const { problem, sol } = await callBackendAPI('getProblem', {difficulty: difficulty});
    const id = getRandomInt(this.props.problems[difficulty].length);
    const { problem, sol } = this.props.problems[difficulty][id];
    for (let i = 0; i < 9; i++) {
      for (let j = 0; j < 9; j++) {
        const value = problem[i][j], solution = sol[i][j];
        board[i][j] = {
          value: value || null,
          solution: solution,
          prefilled: Boolean(value),
          notes: Array(10).fill(false)
        };
        if (value) {
          const [r, c, s] = getRCSOfCoordinate([i, j]);
          this.rowCount[r][value]++;
          this.colCount[c][value]++;
          this.sqrCount[s][value]++;
          this.filledCount[value]++;
        }
      }
    }
    if (this.refs.timer) {
      this.refs.timer.play();
    }
    this.setState({
      board: board,
      selectedPos: null,
      success: false,
      noteMode: false,
      chooseDifficultyMode: false,
    });
  };

  getSelected = () => {
    if (this.state.selectedPos){
      const [x, y] = this.state.selectedPos;
      return this.state.board[x][y];
    }
    return null;
  };

  selectCellFunc = (cellPos) => {
    this.setState({'selectedPos': cellPos});
  };

  addNote = (number) => {
    const { board, selectedPos } = this.state;
    const [x, y] = selectedPos;
    const newBoard = JSON.parse(JSON.stringify(board));
    newBoard[x][y].notes[number] = !newBoard[x][y].notes[number];
    this.setState({'board': newBoard});
  };

  fillNumber = (number) => {
    const { board, selectedPos } = this.state;
    const [x, y] = selectedPos;
    const [r, c, s] = getRCSOfCoordinate(selectedPos);
    const oldNumber = board[x][y].value;
    const newBoard = JSON.parse(JSON.stringify(board));
    if(oldNumber === number) {
      number = null;
    }
    if (oldNumber) {
      this.rowCount[r][oldNumber]--;
      this.colCount[c][oldNumber]--;
      this.sqrCount[s][oldNumber]--;
      this.filledCount[oldNumber]--;
    }
    newBoard[x][y].notes = Array(10).fill(false);
    newBoard[x][y].value = number;
    if (number) {
      this.rowCount[r][number]++;
      this.colCount[c][number]++;
      this.sqrCount[s][number]++;
      this.filledCount[number]++;
    }
    this.setState({'board': newBoard});
  };

  giveHint = () => {
    if (this.hintsLeft === 0 && this.getExtraHint) {
      this.getExtraHint();
      this.hintsLeft++;
    }
    if (this.hintsLeft > 0) {
      this.hintsLeft--;
      const { board, selectedPos } = this.state;
      const [x, y] = selectedPos;
      this.fillNumber(board[x][y].solution);
    }
  };

  newGame = () => {
    this.setState({ chooseDifficultyMode: true });
  };

  shareResult = async () => {
    if (navigator.share) {
      // const shareDict = {
      //   title: textDict.titleText,
      //   text: String.format(textDict.shareDescriptionText,
      //     textDict.difficultyText[this.difficulty], this.refs.timer.state.displayTime),
      //   url: String.format(Constants.CHANNEL_URL, this.country, Constants.COUNTRY_CHANNEL_ID[this.country]),
      // };
      const shareDict = {
        title: textDict.titleText,
        text: String.format(textDict.shareDescriptionText,
          textDict.difficultyText[this.difficulty], this.refs.timer.state.displayTime) + "\n" +
          String.format(Constants.CHANNEL_URL, this.country, Constants.COUNTRY_CHANNEL_ID[this.country]),
      };
      // if (this.screenshot && navigator.canShare &&
      //   navigator.canShare({ files: [this.screenshot] })) {
      //   shareDict.files = [this.screenshot];
      // }
      await navigator.share(shareDict);

      const value = {
        game: 'sudoku',
        difficulty: this.difficulty,
        finishTime: this.refs.timer.seconds,
        completed: true,
      };
      this.sendToApp('tvGamesShare', value);

    } else {
      window.alert("Sharing is not supported in your device.");
    }
  }

  isConflict = (cellPos, value) => {
    const [r, c, s] = getRCSOfCoordinate(cellPos);
    return (this.rowCount[r][value] > 1) ||
           (this.colCount[c][value] > 1) ||
           (this.sqrCount[s][value] > 1);
  };

  renderHeader() {
    const difficultyStars = {
      Easy: 1,
      Medium: 2,
      Hard: 3,
    };
    return (
      <div className="header">
        <div className="headerBox">
          <Timer ref="timer"/>
        </div>
          <div className="difficulty">
            <div>{textDict.difficultyText[this.difficulty]}</div>
            <div className="stars">
              {
                Array(difficultyStars[this.difficulty]).fill().map(() => {
                  return (<StarIcon/>);
                })
              }
            </div>
          </div>
        <div className="headerBox">
          <div
            className="actionNewGame"
            onClick={this.state.success ? undefined : this.newGame}
            style={{color: Constants.CONTROL_NUMBER_COLOR}}
          >
            <div className="text">
              {textDict.newGameText}
            </div>
            <ArrowIcon/>
          </div>
        </div>
      </div>
    );
  }

  renderChooseDifficulty() {
    return (
      <div className="newGame">
        {
          this.state.chooseDifficultyMode &&
          (<div className="back" onClick={() => {
            this.setState({ chooseDifficultyMode : false });
          }}>
            <ArrowIcon/>
            <div className="text"> {textDict.backText} </div>
          </div>)
        }
        <div className="caption">
        {textDict.chooseDifficultyText}
        </div>
        <div className="button" onClick={() => { this.initialize('easy'); }}>
        {textDict.difficultyText.Easy}
        </div>
        <div className="button" onClick={() => { this.initialize('medium'); }}>
        {textDict.difficultyText.Medium}
        </div>
        <div className="button" onClick={() => { this.initialize('hard'); }}>
        {textDict.difficultyText.Hard}
        </div>
      </div>
    );
  }

  renderCell(cell, cellPos) {
    const { selectedPos } = this.state;
    const selected = this.getSelected();
    const { value, prefilled, notes } = cell;
    const isConflict = this.isConflict(cellPos, value);
    const isPeer = areCoordinatePeers(cellPos, selectedPos);
    const isSameValue = (selected && selected.value
      && value === selected.value);
    const isSelected = JSON.stringify(cellPos) === JSON.stringify(selectedPos);

    this.conflicts += isConflict;

    return (
      <Cell
        value={value}
        notes={notes}
        prefilled={prefilled}
        isSelected={isSelected}
        isConflict={isConflict}
        isPeer={isPeer}
        isSameValue={isSameValue}
        onClick={() => { this.selectCell(cellPos) }}
      />
    );
  }

  renderBoard() {
    const { board } = this.state;
    this.conflicts = 0;
    return (
      <div className="board" ref={this.boardRef}>
          {board.map((row, i) => (
            <div className="row" key={i}>
              {row.map((cell, j) => this.renderCell(cell, [i, j]))}
            </div>
          ))}
      </div>
    );
  }

  renderNumberControl() {
    const selected = this.getSelected();
    const prefilled = selected && selected.prefilled;
    const canFill = (!prefilled && selected);
    let onClickFunc;
    if (this.state.noteMode) {
      onClickFunc = (canFill && !selected.value) ? this.addNote : undefined;
    }
    else {
      onClickFunc = canFill ? this.fillNumber : undefined;
    }
    return (
      <div className="control">
        {Array(9).fill().map((_, i) => {
          const number = i + 1;
          return (
            <NumberControl
              key={number}
              number={number}
              noteMode={this.state.noteMode}
              filledCount={this.filledCount[number]}
              onClick={onClickFunc ? () => { onClickFunc(number) } : undefined}
            />
          );
        })}
      </div>
    );
  }

  renderActionControl() {
    const selected = this.getSelected();
    const prefilled = selected && selected.prefilled;
    const canFill = (!prefilled && selected);
    return (
      <div className="actions">
        <div
          className="action one"
          onClick={canFill ? () => { this.fillNumber(null); } : undefined}
        >
          <EraseIcon/>
          <div className="text">
            {textDict.eraseText}
          </div>
        </div>
        <div
          className="action two"
          onClick={() => {
            this.setState((state) => {
              return { noteMode: !state.noteMode };
            })
          }}
          style={{
            backgroundColor: this.state.noteMode ? Constants.ACTIVE_NOTE_COLOR
                           : undefined
          }}
        >
          <NotesIcon/>
          <div className="text">
            {textDict.notesText}
          </div>
        </div>
        <div
          className="action three"
          onClick={canFill ? this.giveHint : undefined}
        >
          <HintIcon/>
          <div className="text">
            {textDict.hintText}
          </div>
        </div>
      </div>
    );
  }

  renderControls() {
    return (
      <div className="controls" ref={this.controlsRef}>
        {this.renderNumberControl()}
        {this.renderActionControl()}
      </div>
    );
  }

  renderGameCompleted() {
    return (
      <div className="gameCompleted">
        <div className="caption">
          {textDict.gameCompletedCaptionText}
        </div>
        <TrophyIcon/>
        <div className="text">
          {textDict.gameCompletedText[this.difficulty]}
        </div>
        <div className="info">
          {String.format(textDict.gameCompletedInfoText, textDict.difficultyText[this.difficulty],
            this.refs.timer.state.displayTime)}
        </div>
        <div className="button" onClick={() => { this.setState({ board: null }) }}>
          {textDict.onelineNewGameText}
        </div>
        {navigator.share &&
          <div className="share" onClick={this.shareResult}>
            {textDict.shareText}
          </div>
        }
      </div>
    );
  }

  endGame() {
    this.selectCell = () => {};
    this.refs.timer.pause();
    const value = {
      game: 'sudoku',
      difficulty: this.difficulty,
      finishTime: this.refs.timer.seconds,
    };
    this.sendToApp('tvGames', value);
    this.setState({
      selectedPos: null,
      success: true
    });
  }

  async componentDidUpdate() {
    if (
      !this.state.success &&
      this.state.board &&
      ((this.filledCount.reduce((a, b) => a + b, 0) === 81 &&
      this.conflicts === 0) ||
      (this.mode === 'cheat' && this.conflicts))
    ) {
      this.endGame();
    }
    // if (this.state.success) {
    //   try {
    //     const completedElement = document.querySelector('.gameCompleted').cloneNode(true);
    //     completedElement.style.transform = "scale(0.8)";
    //     completedElement.style.fontFamily = Constants.FONT_FAMILY[textDict.language];
    //     completedElement.style.position = 'absolute';
    //     completedElement.style.top = '-10000px';
    //     completedElement.classList.add('cloned');
    //     document.body.appendChild(completedElement);
    //     await html2canvas(completedElement, { backgroundColor: Constants.APP_BG_COLOR }).then(canvas => {
    //       let dataUrl = canvas.toDataURL('image/jpeg');
    //       this.screenshot = dataURItoBlob(dataUrl);
    //       this.screenshot = new File([this.screenshot], 'screenshot.jpeg', { type: this.screenshot.type });
    //     });
    //   } catch (e) {
    //     console.log(e);
    //   }
    // }
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  handleClickOutside = (event) => {
    if (
      this.boardRef.current &&
      this.controlsRef.current &&
      !(this.controlsRef.current.contains(event.target) ||
      this.boardRef.current.contains(event.target))
    ) {
        this.selectCell(null);
    }
  };

  render() {
    return (
      <div className='app' id={textDict.language}>
        {this.state.board && this.renderHeader()}
        {(!this.state.board || this.state.chooseDifficultyMode)
          && this.renderChooseDifficulty()}
        {this.state.board && this.renderBoard()}
        {this.state.board && this.renderControls()}
        {this.state.board && this.state.success && this.renderGameCompleted()}
      </div>
    );
  }
}